import { useMemo } from 'react';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';

const EVENT_TITLES = {
  budget: 'Daily Budget updated',
  bid: 'Default Bid updated',
};

const getEventTitle = (eventType) => EVENT_TITLES[eventType] || 'Campaign updated';

const formatEventDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const pad = (num) => String(num).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const buildMessage = ({ previous_value, new_value }) => {
  if (previous_value == null && new_value == null) return '';
  return `Changed from "${previous_value}" to "${new_value}".`;
};

/**
 * Fetches the campaign changes log for the advertiser's campaigns within the
 * given date range and maps it into a flat list of notifications, ordered from
 * the latest change to the oldest.
 */
export const useNotifications = ({ from, to }) => {
  const hasRange = Boolean(from && to);

  // The changes-log endpoint expects the campaign ids it should report on, so
  // we first resolve the advertiser's campaigns for the selected range.
  const { data: campaignsResult, isFetching: isFetchingCampaigns } =
    saveCampaignServiceAPI.useGetCampaignsByDateQuery({ from, to }, { skip: !hasRange });

  const campaignsIDs = useMemo(() => {
    const campaigns = campaignsResult?.campaignsData;
    if (!campaigns?.length) return '';
    return campaigns.map(({ _campaignid }) => _campaignid).join(',');
  }, [campaignsResult?.campaignsData]);

  const {
    data: changesLog,
    isFetching: isFetchingChanges,
    isError,
  } = saveCampaignServiceAPI.useGetCampaignChangesLogQuery(
    { from, to, campaignsIDs },
    { skip: !hasRange || !campaignsIDs },
  );

  const notifications = useMemo(() => {
    if (!changesLog?.length) return [];

    return changesLog
      .flatMap((group) =>
        (group.changes || []).map((change, index) => ({
          id: `${group.cm_campaignid}-${change.event_date}-${index}`,
          severity: 'info',
          title: getEventTitle(change.event_type),
          date: formatEventDate(change.event_date),
          rawDate: change.event_date,
          message: buildMessage(change),
          changedBy: change.user_name,
          campaign: { name: change.cm_name, id: String(group.cm_campaignid) },
        })),
      )
      .sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
  }, [changesLog]);

  return {
    notifications,
    isLoading: isFetchingCampaigns || isFetchingChanges,
    isError,
  };
};
